// Bank Statement Analyzer (BSA) embed — pure helpers for the loan picker.
//
// Loans come from the access-scoped `search_encompass_loans` RPC, so the list
// already matches what the user may see in Loan Center; these helpers only
// narrow it to files that can still be analyzed and build the embed URL.

/** Same-origin embed, proxied to GPU3 in production and local BSA in development. */
export const ANALYZER_EMBED_PATH = '/bsa/';

/** Public origin liveness; an API auth rejection may come from the proxy alone. */
export const ANALYZER_HEALTH_PATH = '/bsa/healthz';

export interface AnalyzerLoan {
  id: string;
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name?: string | null;
  borrower_last_name?: string | null;
  loan_folder?: string | null;
  milestone?: string | null;
  funded_date?: string | null;
  loan_officer_first_name?: string | null;
  loan_officer_last_name?: string | null;
}

function clean(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

/** Unfunded Active/Prospect files, including the legacy plural folder spelling. */
export function isAnalyzableLoan(loan: AnalyzerLoan): boolean {
  return ['active', 'prospect', 'prospects'].includes(clean(loan.loan_folder).toLowerCase()) && !loan.funded_date
    && Boolean(borrowerDisplayName(loan));
}

export function borrowerDisplayName(loan: AnalyzerLoan): string {
  return [clean(loan.borrower_first_name), clean(loan.borrower_last_name)]
    .filter(Boolean)
    .join(' ');
}

export function loanOfficerDisplayName(loan: AnalyzerLoan): string {
  return [clean(loan.loan_officer_first_name), clean(loan.loan_officer_last_name)]
    .filter(Boolean)
    .join(' ');
}

export function buildAnalyzerEmbedUrl(loan: AnalyzerLoan): string {
  // `embed=portal` switches BSA to its edge-to-edge embedded layout.
  const params = new URLSearchParams({ embed: 'portal' });
  const borrower = borrowerDisplayName(loan);
  if (borrower) {
    params.set('borrower', borrower);
  }
  const loanNumber = clean(loan.trans_details_loan_number);
  if (loanNumber) {
    params.set('loan', loanNumber);
  }
  if (loan.id) {
    params.set('loan_id', loan.id);
  }
  return `${ANALYZER_EMBED_PATH}?${params.toString()}`;
}

/** Identify BSA itself, not a portal HTML fallback or an edge authentication rejection. */
export function isAnalyzerHealthyResponse(status: number, body: unknown): boolean {
  return status === 200 && typeof body === 'object' && body !== null
    && 'ok' in body && body.ok === true && 'service' in body && body.service === 'bsa';
}
